const analyticsPrototype = {
    _isActive: false,
    _isDebugMode: false,
    _isBootstrapped: false,
    _googleIds: {
        tagId: null,
        analyticsId: null,
        analyticsV4Id: null,
    },
    _analyticsQueue: [],
    _isPageTrackingEnabled: true,
    _isNavLinkClickEnabled: true,
    _analyticsV4Debug: false,
    _user: null,
    _organization: null,
    _anonymousId: null,
    _donationDomains: [],

    bootstrap(config = {}) {
        this._isActive = this._normalizeBoolean(config.isActive);
        this._isDebugMode = this._normalizeBoolean(config.isDebugMode);
        this._analyticsV4Debug = this._normalizeBoolean(config.analyticsV4Debug, false);
        this._googleIds = {
            tagId: config.tagId || null,
            analyticsId: config.analyticsId || null,
            analyticsV4Id: config.analyticsV4Id || null,
        };
        this._donationDomains = [config.donationHome, config.donationApp].filter(Boolean);
        this._user = this._normalizeUser(config.user);
        this._organization = this._user ? this._user.organization : null;
        this._anonymousId = this._resolveAnonymousId();
        this.setPageTrackingState(config.isPageTrackingEnabled);
        this.setNavLinkClickState(config.isNavLinkClickEnabled);

        if (!this._isActive) {
            return;
        }

        if (this._isDebugMode) {
            this._isBootstrapped = true;
            this._runQueue();

            return;
        }

        this._bootstrapGoogle();
        this._syncIntercomIdentify();
        this._isBootstrapped = true;
        this._runQueue();
    },

    load() {
        return true;
    },

    ready(callback) {
        if (typeof callback === 'function') {
            callback();
        }
    },

    identify(userId, traits = {}) {
        this._user = this._normalizeUser({
            ...traits,
            id: userId || traits.id || traits.userId || traits.dlaUserId || traits.dl_user_id || null,
        });
        this._organization = this._user ? this._user.organization : null;
        this._syncIntercomIdentify();
    },

    page(name = null, properties = {}) {
        if (typeof name === 'object' && name !== null) {
            this.trackPage(name);

            return;
        }

        this.trackPage({
            category: name,
            name,
            data: properties || {},
        });
    },

    track(payload, legacyPayload = {}) {
        if (!this._isActive) {
            return;
        }

        const normalizedPayload = this._normalizeTrackPayload(payload, legacyPayload);
        if (!normalizedPayload || !normalizedPayload.name) {
            console.error('Track event "name" parameter is required');
            return;
        }

        const mappedPayload = this._buildEventPayload(normalizedPayload);

        if (this._isDebugMode) {
            console.log('Analytics track', mappedPayload);
            return;
        }

        if (!this._isBootstrapped) {
            this._analyticsQueue.push({ type: 'track', payload: normalizedPayload });

            return;
        }

        if (this._hasGoogleId()) {
            window.gtag('event', mappedPayload.eventName, mappedPayload.googlePayload);
        }

        this._trackIntercomEvent(mappedPayload.eventName, mappedPayload.intercomPayload, mappedPayload.forceIntercom);
    },

    trackPage(payload = {}) {
        if (!this._isActive) {
            return;
        }

        const mappedPayload = this._buildPagePayload(payload);

        if (this._isDebugMode) {
            console.log('Analytics page', mappedPayload);
            return;
        }

        if (!this._isBootstrapped) {
            this._analyticsQueue.push({ type: 'trackPage', payload });

            return;
        }

        if (this._hasGoogleId()) {
            window.gtag('event', 'page_view', mappedPayload.googlePayload);
        }

        this._trackIntercomEvent('page_view', mappedPayload.intercomPayload);
    },

    trackLink(elements = [], eventName, callback = null) {
        if (!elements || !eventName) {
            return;
        }

        const bindingAttribute = 'analyticsBound' + eventName.replace(/[^a-z0-9]/gi, '');
        this._forEach(elements, element => {
            if (!element || element.dataset[bindingAttribute]) {
                return;
            }

            element.dataset[bindingAttribute] = '1';
            element.addEventListener('click', () => {
                const eventPayload = typeof callback === 'function' ? callback(element) : {};
                this.track({
                    name: eventName,
                    data: eventPayload || {},
                });
            });
        });
    },

    setPageTrackingState(state = true) {
        this._isPageTrackingEnabled = this._normalizeBoolean(state);
    },

    setNavLinkClickState(state = true) {
        this._isNavLinkClickEnabled = this._normalizeBoolean(state);
    },

    getMetaPageCategory(defaultValue = null) {
        const pageCategory = document.querySelector('meta[property="analytics_page_category"]');

        if (pageCategory && pageCategory.content) {
            return pageCategory.content;
        }

        return defaultValue;
    },

    getMetaPageName(defaultValue = null) {
        const pageName = document.querySelector('meta[property="analytics_page_name"]');

        if (pageName && pageName.content) {
            return pageName.content;
        }

        return defaultValue;
    },

    _bootstrapGoogle() {
        if (!this._hasGoogleId()) {
            return;
        }

        window.dataLayer = window.dataLayer || [];

        if (typeof window.gtag !== 'function') {
            window.gtag = function () {
                window.dataLayer.push(arguments);
            };
        }

        window.gtag('js', new Date());

        if (this._googleIds.tagId) {
            window.gtag('config', this._googleIds.tagId, {
                send_page_view: false,
            });
        }

        if (this._googleIds.analyticsId) {
            window.gtag('config', this._googleIds.analyticsId, {
                groups: 'analytics',
                send_page_view: false,
            });
        }

        if (this._googleIds.analyticsV4Id) {
            const payload = {
                cookie_domain: 'auto',
                debug_mode: this._analyticsV4Debug,
                linker: {
                    domains: this._donationDomains,
                },
                send_page_view: false,
                client_id: this._getResolvedIdentityId(),
                user_id: this._getResolvedIdentityId(),
            };

            window.gtag('config', this._googleIds.analyticsV4Id, payload);
        }

        this._injectAnalyticsScript();
    },

    _runQueue() {
        this._trackPageVisitListener();
        this._trackingNavLinkClickListener();

        if (!this._analyticsQueue.length) {
            return;
        }

        this._analyticsQueue.forEach(item => {
            if (item.type === 'track') {
                this.track(item.payload);
            }

            if (item.type === 'trackPage') {
                this.trackPage(item.payload);
            }
        });
        this._analyticsQueue = [];
    },

    _trackPageVisitListener() {
        if (!this._isPageTrackingEnabled) {
            return;
        }

        if (document.location.pathname === '/') {
            if (this._user) {
                this.trackPage({
                    category: 'Dashboard',
                    data: {
                        path: '/dashboard',
                    },
                });
            } else {
                this.trackPage({
                    category: 'Index',
                    data: {
                        path: '/index',
                    },
                });
            }

            return;
        }

        this.trackPage({
            category: this.getMetaPageCategory(),
            name: this.getMetaPageName() || this.getMetaPageCategory(),
        });
    },

    _trackingNavLinkClickListener() {
        if (!this._isNavLinkClickEnabled) {
            return;
        }

        const bindTracking = () => {
            const links = document.getElementsByClassName('trackable-link');

            this.trackLink([].slice.call(links), 'Navigation Link Clicked', function (element) {
                return {
                    link: element.getAttribute('data-name'),
                    url: element.getAttribute('href'),
                };
            });
        };

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', bindTracking, { once: true });

            return;
        }

        bindTracking();
    },

    _buildEventPayload(payload) {
        const eventPayload = this._cloneValue(payload) || {};
        const forceIntercom = this._normalizeBoolean(eventPayload.intercomEvenIfAnonymous, false);
        const includeUser = this._normalizeBoolean(eventPayload.includeUser, false);
        const eventName = eventPayload.name;

        delete eventPayload.intercomEvenIfAnonymous;
        delete eventPayload.includeUser;
        delete eventPayload.googleName;
        delete eventPayload.googleData;
        delete eventPayload.intercomName;
        delete eventPayload.intercomData;
        delete eventPayload.name;

        const rawProperties = this._cloneValue(eventPayload.data || eventPayload) || {};
        if (includeUser && this._user) {
            rawProperties.user = {
                firstName: this._user.firstName,
                lastName: this._user.lastName,
                email: this._user.email,
                id: this._user.dlaUserId,
            };
        }

        const canonicalPayload = this._buildCanonicalPayload(eventName, rawProperties, false);

        return {
            canonicalPayload,
            eventName,
            googlePayload: this._buildGoogleTrackPayload(canonicalPayload),
            intercomPayload: this._buildIntercomEventPayload(canonicalPayload),
            forceIntercom,
        };
    },

    _buildPagePayload(payload = {}) {
        const rawPayload = this._cloneValue(payload) || {};
        const pagePayload = this._cloneValue(rawPayload.data || rawPayload) || {};

        if (rawPayload.category && !pagePayload.category) {
            pagePayload.category = rawPayload.category;
        }

        if (rawPayload.name && !pagePayload.name) {
            pagePayload.name = rawPayload.name;
        }

        if (!pagePayload.path) {
            pagePayload.path = document.location.pathname;
        }

        if (!pagePayload.title) {
            pagePayload.title = pagePayload.name || document.title;
        }

        if (pagePayload.name && !pagePayload.entityTitle) {
            pagePayload.entityTitle = pagePayload.name;
        }

        const canonicalPayload = this._buildCanonicalPayload('page_view', pagePayload, true);

        return {
            canonicalPayload,
            googlePayload: this._buildGooglePagePayload(canonicalPayload),
            intercomPayload: this._buildIntercomEventPayload(canonicalPayload),
        };
    },

    _buildCanonicalPayload(eventName, properties = {}, isPageEvent = false) {
        const timestamp = Date.now();
        const pageTitle = properties.title || properties.name || document.title || null;
        const pagePath = properties.path || document.location.pathname;

        return {
            type: isPageEvent ? 'page' : 'track',
            event: eventName,
            timestamp: new Date(timestamp).toISOString(),
            timestampUnix: Math.floor(timestamp / 1000),
            userId: this._getUserId(),
            anonymousId: this._getAnonymousId(),
            traits: this._buildTraits(),
            properties: properties || {},
            context: {
                page: {
                    url: document.location.href,
                    path: pagePath,
                    title: pageTitle,
                    referrer: document.referrer || null,
                },
            },
        };
    },

    _buildGoogleTrackPayload(canonicalPayload) {
        const payload = {
            client_id: this._getResolvedIdentityId(),
            user_id: this._getResolvedIdentityId(),
            engagement_time_msec: 1,
        };
        const mappedProperties = {
            Category: canonicalPayload.properties.category,
            entityId: canonicalPayload.properties.entityId,
            entityTitle: canonicalPayload.properties.entityTitle,
            entityType: canonicalPayload.properties.entityType,
        };

        this._forEach(mappedProperties, (value, key) => {
            const normalizedValue = this._sanitizeGoogleValue(value);
            if (typeof normalizedValue === 'undefined') {
                return;
            }

            payload[key] = normalizedValue;
        });

        return payload;
    },

    _buildGooglePagePayload(canonicalPayload) {
        return {
            client_id: this._getResolvedIdentityId(),
            user_id: this._getResolvedIdentityId(),
            page_location: canonicalPayload.context.page.url,
            page_referrer: canonicalPayload.context.page.referrer || undefined,
            page_title: canonicalPayload.context.page.title,
            engagement_time_msec: 1,
        };
    },

    _buildIntercomEventPayload(canonicalPayload) {
        const metadata = {};
        const mappedProperties = {
            category: canonicalPayload.properties.category,
            entityTitle: canonicalPayload.properties.entityTitle || canonicalPayload.properties.name,
            entityID: canonicalPayload.properties.entityId,
        };

        this._forEach(mappedProperties, (value, key) => {
            const normalizedValue = this._sanitizeIntercomValue(value);
            if (typeof normalizedValue === 'undefined') {
                return;
            }

            metadata[key] = normalizedValue;
        });

        return metadata;
    },

    _buildIntercomIdentifyPayload() {
        const userId = this._getUserId();
        const traits = this._buildTraits();

        if (!userId || !traits.email) {
            return null;
        }

        return {
            user_id: userId,
            email: traits.email,
            name: this._buildFullName(traits.firstName, traits.lastName),
            last_request_at: Math.floor(Date.now() / 1000),
        };
    },

    _syncIntercomIdentify() {
        const intercomPayload = this._buildIntercomIdentifyPayload();
        if (!intercomPayload || typeof Intercom === 'undefined') {
            return;
        }

        try {
            Intercom('update', intercomPayload);
        } catch (error) {
            console.error('Intercom analytics identify failed');
        }
    },

    _buildTraits() {
        return {
            email: this._user && this._user.email && this._user.email.includes('@') ? this._user.email : null,
            firstName: this._user ? this._user.firstName : null,
            lastName: this._user ? this._user.lastName : null,
        };
    },

    _getResolvedIdentityId() {
        return this._getUserId() || this._getAnonymousId();
    },

    _getUserId() {
        if (!this._user || !this._user.dlaUserId) {
            return null;
        }

        return String(this._user.dlaUserId);
    },

    _getAnonymousId() {
        if (!this._anonymousId) {
            this._anonymousId = this._resolveAnonymousId();
        }

        return this._anonymousId;
    },

    _resolveAnonymousId() {
        const storageKey = 'donation_analytics_anonymous_id';
        let anonymousId = null;

        try {
            anonymousId = window.localStorage ? window.localStorage.getItem(storageKey) : null;
        } catch (error) {
            anonymousId = null;
        }

        if (!anonymousId) {
            anonymousId = this._createAnonymousId();

            try {
                if (window.localStorage) {
                    window.localStorage.setItem(storageKey, anonymousId);
                }
            } catch (error) {
                // local storage fallback is optional
            }
        }

        return anonymousId;
    },

    _createAnonymousId() {
        const randomPart = Math.random().toString(36).slice(2, 12);

        return 'anon_' + Date.now() + '_' + randomPart;
    },

    _injectAnalyticsScript() {
        const primaryId = this._getPrimaryGoogleId();
        const scriptAttribute = 'data-donation-analytics-script';

        if (!primaryId || document.querySelector('script[' + scriptAttribute + ']')) {
            return;
        }

        const script = document.createElement('script');
        script.type = 'text/javascript';
        script.async = true;
        script.src = 'https://www.googletagmanager.com/gtag/js?id=' + encodeURIComponent(primaryId);
        script.setAttribute(scriptAttribute, '1');

        const firstScript = document.getElementsByTagName('script')[0];
        if (firstScript && firstScript.parentNode) {
            firstScript.parentNode.insertBefore(script, firstScript);

            return;
        }

        document.head.appendChild(script);
    },

    _trackIntercomEvent(eventName, payload, forceIntercom = false) {
        if (!forceIntercom && !this._getUserId()) {
            return;
        }

        if (typeof Intercom === 'undefined') {
            return;
        }

        try {
            Intercom('trackEvent', eventName, payload || {});
        } catch (error) {
            console.error('Intercom analytics event failed');
        }
    },

    _normalizeTrackPayload(payload, legacyPayload = {}) {
        if (typeof payload === 'string') {
            return {
                name: payload,
                data: legacyPayload || {},
            };
        }

        if (!payload || typeof payload !== 'object') {
            return null;
        }

        return payload;
    },

    _normalizeUser(user) {
        if (!user || typeof user !== 'object') {
            return null;
        }

        const id = user.dlaUserId || user.dl_user_id || user.userId || user.id || null;
        const organization = Array.isArray(user.organizations) && user.organizations.length ? user.organizations[0] : null;

        return {
            dlaUserId: id ? String(id) : null,
            firstName: user.firstName || user.first_name || user.name || null,
            lastName: user.lastName || user.last_name || null,
            email: this._isValidEmail(user.email) ? user.email : null,
            organization: organization || null,
        };
    },

    _getPrimaryGoogleId() {
        return this._googleIds.tagId || this._googleIds.analyticsId || this._googleIds.analyticsV4Id || null;
    },

    _hasGoogleId() {
        return Boolean(this._getPrimaryGoogleId());
    },

    _buildFullName(firstName, lastName) {
        return [firstName, lastName].filter(Boolean).join(' ').trim() || null;
    },

    _sanitizeGoogleValue(value) {
        if (this._isNil(value) || value === '') {
            return undefined;
        }

        if (typeof value === 'boolean') {
            return value ? 'true' : 'false';
        }

        if (typeof value === 'number' || typeof value === 'string') {
            return value;
        }

        try {
            return JSON.stringify(value);
        } catch (error) {
            return String(value);
        }
    },

    _sanitizeIntercomValue(value) {
        if (this._isNil(value) || value === '') {
            return undefined;
        }

        if (typeof value === 'number' || typeof value === 'string' || typeof value === 'boolean') {
            return value;
        }

        try {
            return JSON.stringify(value);
        } catch (error) {
            return String(value);
        }
    },

    _normalizeBoolean(value, defaultValue = true) {
        if (typeof value === 'undefined' || value === null) {
            return defaultValue;
        }

        if (typeof value === 'boolean') {
            return value;
        }

        if (typeof value === 'string') {
            return ['1', 'true', 'yes', 'on'].includes(value.toLowerCase());
        }

        return Boolean(value);
    },

    _isValidEmail(value) {
        return typeof value === 'string' && value.includes('@');
    },

    _isNil(value) {
        return value === null || typeof value === 'undefined';
    },

    _cloneValue(value) {
        if (this._isNil(value)) {
            return value;
        }

        return JSON.parse(JSON.stringify(value));
    },

    _forEach(collection, callback) {
        if (Array.isArray(collection)) {
            collection.forEach((value, index) => callback(value, index));

            return;
        }

        Object.keys(collection || {}).forEach(key => {
            callback(collection[key], key);
        });
    },
};

window.donation_analytics = Object.create(analyticsPrototype);
window.analytics = window.donation_analytics;
